import React, {
  useEffect,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

// Components
import InputPanel from '../../components/InputPanel/InputPanel';
import RateComparisonChart from '../../components/RateComparisonChart/RateComparisonChart';
import SelectCurrentRate from '../../components/InputComponents/SelectCurrentRate/SelectCurrentRate';
import UserPrefsContext from '../../../context/UserPrefs/UserPrefsContext';
import SelectRateComparisonVehicle from '../../components/InputComponents/SelectRateComparisonVehicle/SelectRateComparisonVehicle';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import SlideMilesDrivenAnnually from '../../../components/InputComponents/SlideMilesDrivenAnnually/SlideMilesDrivenAnnually';
import SelectChargingPattern from '../../components/InputComponents/SelectChargingPattern/SelectChargingPattern';
import SlideElectricMilesPortionForPhev from '../../../components/InputComponents/SlideElectricMilesPortionForPhev/SlideElectricMilesPortionForPhev';
import SlideCurrentMonthlyBill from '../../components/InputComponents/SlideCurrentMonthlyBill/SlideCurrentMonthlyBill';
import SlideGasolinePrice from '../../../components/InputComponents/SlideGasolinePrice/SlideGasolinePrice';
import SlideEquivalentMilesPerGallon from '../../components/InputComponents/SlideEquivalentMilesPerGallon/SlideEquivalentMilesPerGallon';
import SlidePublicChargingPercentage from '../../components/InputComponents/SlidePublicChargingPercentage/SlidePublicChargingPercentage';
import SelectPublicChargingCost from '../../components/InputComponents/SelectPublicChargingCost/SelectPublicChargingCost';

// Data
import rates from '../../data/rates';
import {
  chargingPatternsJuneToAug,
  chargingPatternsSepToMay,
  combinedLoadProfile,
} from '../../data/chargingPatterns';
import zipcodeToBaselineAllocationRegion from '../../data/zipcodeToBaselineAllocationRegion.json';

// Functions
import isPHEV from '../../../functions/vehicle/isPHEV';
import { FormatAsCents } from '../../../utils/Helpers/Format';
import getLoadProfileData from '../../data/loadProfiles/getLoadProfileData';
import totalElectricVehicleKwhPerYear from '../../functions/totalElectricVehicleKwhPerYear';
import RateCostCalculator from '../../functions/RateCostCalculator';
import GasolineCostCalculator from '../../functions/GasolineCostCalculator';
import rateComparisonChartData from '../../functions/rateComparisonChartData';

// Components
import RateComparisonBanner from '../../components/RateComparisonBanner/RateComparisonBanner';
import RateDetails from '../../components/RateDetails/RateDetails';
import RateOptionsWizard from '../../components/RateOptionsWizard/RateOptionsWizard';

// Styles
import './RateComparison.scss';

import { useIntl } from 'react-intl';
import useRateTotals from './useRateTotals';
import RLMSDetails from '../../data/rates/RLMSDetails';

const sortRateTotals = (rateTotals, orderArray) => {
  return [...rateTotals].sort((a, b) => {
    const indexA = orderArray.indexOf(a.id.replace('-current', ''));
    const indexB = orderArray.indexOf(b.id.replace('-current', ''));
    return indexA - indexB;
  });
};

const RateComparison = ({ electricVehicles, ip, uuid }) => {
  const intl = useIntl();
  useEffect(() => {
    document.title =
      process.env.REACT_APP_PAGES_RATES_TITLE || 'National Grid Rate Advisor';
  });

  const userPrefs = useContext(UserPrefsContext);
  const {
    isComparingLowestRateOnly,
    vehicleIdForRateComparison,
    chargingPatternsJuneToAugId,
    chargingPatternsSepToMayId,
    currentRateId,
    zipcode,
    hasSolarAtHome,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    publicChargingPercentage,
    publicChargingCostInCentsPerKwh,
    currentMonthlyBill,
    gasolinePriceInCentsPerGal,
    equivalentMilesPerGallon,
    selectedEnergyCostIds,
    currentRateClimateZone,
  } = userPrefs.get();

  const [selectedVehicle, setSelectedVehicle] = useState();
  const [bestSaving, setBestSaving] = useState();

  const setBestSavingCallback = useCallback((saving) => {
    setBestSaving(saving);
  }, []);

  useEffect(() => {
    if (!electricVehicles || electricVehicles.length === 0) return;

    if (vehicleIdForRateComparison) {
      const matchedVehicle = electricVehicles.find(
        (ev) => ev.electric_vehicle_id === vehicleIdForRateComparison
      );
      if (matchedVehicle) {
        setSelectedVehicle(matchedVehicle);
        return;
      }
    }

    setSelectedVehicle(electricVehicles[0]);
  }, [electricVehicles, vehicleIdForRateComparison]);

  const currentChargingPatternSepMay = useMemo(
    () =>
      chargingPatternsSepToMay.find((p) => p.id === chargingPatternsSepToMayId) ||
      chargingPatternsSepToMay[0],
    [chargingPatternsSepToMayId]
  );

  const currentChargingPatternJunAug = useMemo(
    () =>
      chargingPatternsJuneToAug.find(
        (p) => p.id === chargingPatternsJuneToAugId
      ) || chargingPatternsJuneToAug[0],
    [chargingPatternsJuneToAugId]
  );

  const combinedChargingPatternLoadProfile = useMemo(
    () =>
      combinedLoadProfile(
        currentChargingPatternSepMay.loadProfile,
        currentChargingPatternJunAug.loadProfile
      ),
    [
      currentChargingPatternSepMay.loadProfile,
      currentChargingPatternJunAug.loadProfile,
    ]
  );

  const currentTerritory = useMemo(
    () => zipcodeToBaselineAllocationRegion[zipcode] || '8',
    [zipcode]
  );

  const currentRate = useMemo(
    () =>
      Object.values(rates).find((e) => e.id === currentRateId) ||
      rates[currentRateId] ||
      rates[Object.keys(rates)[0]],
    [currentRateId]
  );

  const evaluatedCurrentRate = useMemo(
    () => ({
      ...currentRate,
      data: currentRate.data(currentTerritory, currentRateClimateZone),
    }),
    [currentRate, currentTerritory, currentRateClimateZone]
  );

  const homeLoadProfile = useMemo(
    () =>
      getLoadProfileData({
        rateGroup: currentRateId,
        climateZone: currentRateClimateZone,
        hasSolar: hasSolarAtHome,
      }),
    [currentRateId, currentRateClimateZone, hasSolarAtHome]
  );

  const totalElectricVehicleKwh = useMemo(
    () =>
      totalElectricVehicleKwhPerYear({
        kwhPer100Miles: selectedVehicle?.electric_efficiency || 0,
        milesDrivenAnnually,
        percentageDrivenElectric: isPHEV(selectedVehicle)
          ? electricMilesPortionForPhev
          : 100,
      }),
    [selectedVehicle, milesDrivenAnnually, electricMilesPortionForPhev]
  );

  const homeChargingKwh = useMemo(
    () => totalElectricVehicleKwh * ((100 - publicChargingPercentage) / 100),
    [totalElectricVehicleKwh, publicChargingPercentage]
  );

  const publicChargingElectricCostPerMonth = useMemo(
    () =>
      (totalElectricVehicleKwh *
        (publicChargingCostInCentsPerKwh / 100) *
        (publicChargingPercentage / 100)) /
      12,
    [
      totalElectricVehicleKwh,
      publicChargingCostInCentsPerKwh,
      publicChargingPercentage,
    ]
  );

  const rateCostCalculator = useMemo(
    () =>
      new RateCostCalculator({
        typicalHomeLoadProfile: homeLoadProfile,
        currentRate: evaluatedCurrentRate,
        currentMonthlyBill,
        homeChargingKwh,
        chargingPatternLoadProfile: combinedChargingPatternLoadProfile,
      }),
    [
      homeLoadProfile,
      evaluatedCurrentRate,
      currentMonthlyBill,
      homeChargingKwh,
      combinedChargingPatternLoadProfile,
    ]
  );

  const gasolineCostCalculator = useMemo(
    () =>
      new GasolineCostCalculator({
        selectedVehicle,
        gasolinePriceInCentsPerGal,
        milesDrivenAnnually,
        electricMilesPortionForPhev,
        equivalentMilesPerGallon,
      }),
    [
      selectedVehicle,
      gasolinePriceInCentsPerGal,
      milesDrivenAnnually,
      electricMilesPortionForPhev,
      equivalentMilesPerGallon,
    ]
  );

  const evaluatedRates = useMemo(
    () =>
      Object.keys(rates).map((k) => ({
        ...rates[k],
        data: rates[k].data(currentTerritory, currentRateClimateZone),
      })),
    [currentTerritory, currentRateClimateZone]
  );

  const { isLoading, rateTotals } = useRateTotals({
    evaluatedRates,
    evaluatedCurrentRate,
    rateCostCalculator,
    gasolineCostCalculator,
    publicChargingElectricCostPerMonth,
    selectedEnergyCostIds,
    isComparingLowestRateOnly,
  });

  const { labelHome, labelElectric, labelGasoline } = useMemo(
    () => ({
      labelHome: intl.formatMessage({
        id: 'savingsGraphHomeLabel',
        defaultMessage: 'Home',
      }),
      labelElectric: intl.formatMessage({
        id: 'savingsGraphEVLabel',
        defaultMessage: 'Electric Vehicle',
      }),
      labelGasoline: intl.formatMessage({
        id: 'savingsGraphGasLabel',
        defaultMessage: 'Gasoline',
      }),
    }),
    [intl]
  );

  const ratesShowInSelector = useMemo(
    () => Object.keys(rates).filter((key) => key === 'SC-1' || key === 'VTOU'),
    []
  );

  // keep the rate sorted in the order we want to show them
  // Also includes EV-SC-1 that we are not calculating with the rates library
  const ratesShowInDetail = useMemo(
    () => [
      rates['SC-1'],
      {
        id: 'EV-SC-1',
        title: ['EV Charge Smart Plan with SC-1'],
        subTitle: [''],
        detailsComponent: RLMSDetails,
      },
      rates['VTOU'],
    ],
    []
  );

  const sortedRateTotals = useMemo(() => {
    if (!rateTotals || rateTotals.length === 0) return [];
    const orderArray = ['VTOU', 'EV-SC-1-VTOU', 'SC-1', 'EV-SC-1'];
    const orderArraySc1 = ['SC-1', 'SC-1-EV', 'EV-SC-1', 'VTOU'];
    let modifiedRateTotals = [...rateTotals];

    // get sc-1 id based on currentRate
    const sc1Id = currentRateId === 'SC-1' ? 'SC-1-current' : 'SC-1';

    const sc1 = modifiedRateTotals.find((rate) => rate.id === sc1Id);

    // get gasoline cost from rateTotals
    const gasolineCost = modifiedRateTotals[1].gas || 0;

    // Add SC-1-EV when currentRateId is SC-1
    if (currentRateId === 'SC-1') {
      const sc1EV = {
        ...sc1,
        gas: gasolineCost,
        ev: sc1?.evCostValue,
        home: sc1?.home,
        id: 'SC-1-EV',
        title: ['SC-1 with EV'],
      };
      modifiedRateTotals.push(sc1EV);
    }

    // Add EV-SC-1 with 288 less than SC-1 for discount 24 per month
    const evSc1 = {
      ...sc1,
      gas: gasolineCost,
      ev: sc1?.evCostValue - 288,
      evCostValue: sc1?.evCostValue - 288,
      home: sc1?.home,
      id: 'EV-SC-1',
      title: ['EV Charge Smart Plan with SC-1'],
    };
    modifiedRateTotals.push(evSc1);

    // Filter out EV-SC-1-VTOU when currentRateId is SC-1 and add SC-1-EV
    if (currentRateId === 'SC-1') {
      modifiedRateTotals = modifiedRateTotals.filter(
        (rate) => rate.id !== 'EV-SC-1-VTOU'
      );
    }

    // Handle VTOU special case
    if (currentRateId === 'VTOU') {
      const currentRate = modifiedRateTotals.find((rate) =>
        rate.id.includes('(Current)*')
      );
      const vtouRate = modifiedRateTotals.find(
        (rate) => rate.id === 'EV-SC-1-VTOU'
      );

      if (currentRate && vtouRate) {
        vtouRate.title = ['VTOU with EV'];
        vtouRate.home = currentRate.home;
        vtouRate.ev = currentRate.ev;
        vtouRate.total = currentRate.total;
        vtouRate.gas = gasolineCost;
      }
    }

    // Here we changed the name and the values according the POs request in the CONSUMER-6666
    // Modify titles based on currentRateId
    modifiedRateTotals = modifiedRateTotals.map((rate) => {
      const modifiedRate = { ...rate };
      if (currentRateId === 'SC-1' && rate.id === 'VTOU') {
        modifiedRate.title = ['VTOU with EV'];
      } else if (currentRateId === 'VTOU' && rate.id === 'SC-1') {
        modifiedRate.title = ['SC-1 with EV'];
      } else if (currentRateId === 'VTOU' && rate.id === 'EV-SC-1-VTOU') {
        const currentRate = modifiedRateTotals.find((rateItem) =>
          rateItem.id.includes('-current')
        );

        if (currentRate) {
          modifiedRate.title = ['VTOU with EV'];
          modifiedRate.home = currentRate.home;
          modifiedRate.ev = currentRate.evCostValue;
          modifiedRate.gas = gasolineCost;
        }
      }
      return modifiedRate;
    });

    return currentRateId === 'VTOU'
      ? sortRateTotals(modifiedRateTotals, orderArray)
      : sortRateTotals(modifiedRateTotals, orderArraySc1);
  }, [rateTotals, currentRateId]);

  const chartData = useMemo(
    () =>
      rateComparisonChartData({
        rateTotals: sortedRateTotals,
        labelHome,
        labelElectric,
        labelGasoline,
      }),
    [sortedRateTotals, labelHome, labelElectric, labelGasoline]
  );

  return (
    <section className="container RateAdvisor">
      <div className="row">
        <div className="col-sm-12 text-center">
          <h1 className="header">
            <FormattedMessage
              id="rateAdvisor.title"
              defaultMessage="Rate Advisor"
            />
          </h1>
          <h2 className="small-centered mb-bottom sub-header">
            <FormattedMessage
              id="rateAdvisor.sub"
              defaultMessage="Identify the lowest-cost electric rate for your EV. Adjust the inputs below to find the right electric rate for you."
              description="Rate Advisor Subtitle"
            />
          </h2>
        </div>
      </div>

      {!electricVehicles ||
      sortedRateTotals?.length === 0 ||
      !selectedVehicle ? (
        <div className="text-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="row">
          <div className="col-md-3">
            <RateOptionsWizard
              electricVehicles={electricVehicles}
              rates={evaluatedRates}
              rateTotals={sortedRateTotals}
              selectedVehicle={selectedVehicle}
              utilityName="SCE"
              typicalDriverName="Californian"
              mostCommonRateName={rates[Object.keys(rates)[0]].title}
              bestSaving={bestSaving}
            />
            <InputPanel
              title={
                intl.formatMessage
                  ? intl.formatMessage({
                      id: 'basicFilters',
                      defaultMessage: 'Basic Filters',
                    })
                  : 'Basic Filters'
              }
            >
              <SelectCurrentRate
                rates={ratesShowInSelector.map((key) => rates[key])}
              />
              <SelectRateComparisonVehicle
                electricVehicles={electricVehicles}
                selectedVehicle={selectedVehicle}
              />
              <SlideMilesDrivenAnnually />
              <SelectChargingPattern
                chargingPatterns={chargingPatternsJuneToAug}
                chargingPatternsId={'chargingPatternsJuneToAugId'}
                label={intl.formatMessage({
                  id: 'chargingPatternJuneToAugust',
                  defaultMessage: 'Charging Pattern: Jun - Aug',
                })}
              />

              <SelectChargingPattern
                chargingPatterns={chargingPatternsSepToMay}
                chargingPatternsId={'chargingPatternsSepToMayId'}
                label={intl.formatMessage({
                  id: 'chargingPatternSepToMay',
                  defaultMessage: 'Charging Pattern: Sep - May',
                })}
              />
            </InputPanel>
            <br />
            <InputPanel
              title={
                intl.formatMessage
                  ? intl.formatMessage({
                      id: 'homeAssumptions',
                      defaultMessage: 'Home Assumptions',
                    })
                  : 'Home Assumptions'
              }
              className="mb-4"
              defaultIsCollapsed={false}
            >
              <SlideCurrentMonthlyBill id="monthly-slider-filters" />
            </InputPanel>
            <InputPanel
              title={
                intl.formatMessage
                  ? intl.formatMessage({
                      id: 'drivingAssumptions',
                      defaultMessage: 'Driving Assumptions',
                    })
                  : 'Driving Assumptions'
              }
              className="mb-4"
              defaultIsCollapsed={false}
            >
              {isPHEV(selectedVehicle) && (
                <SlideElectricMilesPortionForPhev
                  label={
                    intl.formatMessage
                      ? intl.formatMessage({
                          id: 'electricPortion',
                          defaultMessage: 'Electric Portion',
                        })
                      : 'Electric Portion'
                  }
                  description={(val) =>
                    `${val}% electric / ${100 - val} % gasoline`
                  }
                />
              )}
              <SlideGasolinePrice
                label={
                  intl.formatMessage
                    ? intl.formatMessage({
                        id: 'gasolinePrice',
                        defaultMessage: 'Gasoline Price',
                      })
                    : 'Gasoline Price'
                }
                description={(val) => `${FormatAsCents(val / 100)}/gallon`}
              />
              <SlideEquivalentMilesPerGallon />
              <SlidePublicChargingPercentage />
              <SelectPublicChargingCost />
            </InputPanel>
          </div>
          <div className="col-md-9">
            <div className="rate-comparison-title">
              <h3>
                <FormattedMessage
                  id="rateAdvisor.rateComparison"
                  defaultMessage="Annual Estimated Electricity and Vehicle Fueling Cost Comparison"
                  description="Rate Comparison Title"
                />
              </h3>
            </div>
            <RateComparisonBanner
              rateTotals={sortedRateTotals}
              selectedVehicle={selectedVehicle}
              bestSaving={bestSaving}
            />
            <RateComparisonChart
              title={
                intl.formatMessage
                  ? intl.formatMessage({
                      id: 'pricingPlan',
                      defaultMessage: 'Pricing Plan Comparison',
                    })
                  : 'Pricing Plan Comparison'
              }
              chartData={chartData}
              className="d-md-block"
              setBestSaving={setBestSavingCallback}
              isLoading={isLoading}
            />
            <div className="mt-4">
              <RateDetails
                rates={ratesShowInDetail}
                selectedRateDetailsId={userPrefs.get('selectedRateDetailsId')}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default RateComparison;

RateComparison.propTypes = {
  electricVehicles: PropTypes.array,
  ip: PropTypes.string,
  uuid: PropTypes.string,
};
